/**
 * External dependencies
 */
import type { Attributes } from '../../../../../packages/conversion-action-library/click/types';

/**
 * Internal dependencies
 */
import { domReady } from '../../utils/helpers/dom-ready';
import type { ConvertibleAction, Convert } from '../../types';

const MIN_SCREEN_COVERAGE = 0.2;
const MIN_INTERSECTION_RATIO = 0.5;

export function listenToElementViewEvent(
	allActions: ReadonlyArray< ConvertibleAction >,
	convert: Convert
): void {
	const actions = allActions.filter( isElementViewAction );
	if ( ! actions.length ) {
		return;
	}

	const actionsBySelector = actions.reduce(
		( result, action ) => {
			const key = getSelector( action.attributes );
			result[ key ] = [ ...( result[ key ] ?? [] ), action ];
			return result;
		},
		{} as Record< string, ReadonlyArray< ConvertibleAction< Attributes > > >
	);

	domReady( () =>
		Object.keys( actionsBySelector ).forEach( ( selector ) => {
			const removeListener = onElementVisible( selector, () => {
				removeListener();
				convert( actionsBySelector[ selector ] ?? [] );
			} );
		} )
	);
}

// =======
// HELPERS
// =======

function isElementViewAction(
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > {
	return 'nab/element-view' === action.type;
}

function getSelector( attrs: Attributes ): string {
	switch ( attrs.mode ) {
		case 'css':
			return attrs.value;
		case 'id':
			return `#${ attrs.value }`;
		case 'class':
			return `.${ attrs.value }`;
	}
}

type VisibilityCallback = ( el: Element ) => void;

function onElementVisible(
	selector: string,
	callback: VisibilityCallback,
	options: IntersectionObserverInit = {}
) {
	const intersectionObservers = new Map< Element, IntersectionObserver >();

	const observeVisibility = ( el: Element ) => {
		const io = new IntersectionObserver(
			( entries, observer ) => {
				for ( const entry of entries ) {
					const {
						intersectionRatio,
						boundingClientRect,
						isIntersecting,
					} = entry;

					const visibleEnough =
						( isIntersecting &&
							intersectionRatio >= MIN_INTERSECTION_RATIO ) ||
						screenCoverage( boundingClientRect ) >=
							MIN_SCREEN_COVERAGE;

					if ( visibleEnough ) {
						callback( entry.target );
						observer.disconnect();
					}
				}
			},
			{
				...options,
				threshold: [ 0, MIN_INTERSECTION_RATIO ],
			}
		);
		io.observe( el );
		intersectionObservers.set( el, io );
	};

	const mutationObserver = new MutationObserver( () => {
		const elements = document.querySelectorAll( selector );
		for ( const el of elements ) {
			if ( ! intersectionObservers.has( el ) ) {
				observeVisibility( el );
			}
		}
	} );

	mutationObserver.observe( document.body, {
		childList: true,
		subtree: true,
	} );

	// Check in case the element already exists on load
	const initialElements = document.querySelectorAll( selector );
	initialElements.forEach( ( el ) => {
		observeVisibility( el );
	} );

	return () => {
		mutationObserver.disconnect();
		intersectionObservers.forEach( ( io ) => io.disconnect() );
		intersectionObservers.clear();
	};
}

function screenCoverage( rect: DOMRectReadOnly ) {
	const vw = document.documentElement.clientWidth || window.innerWidth;
	const vh = document.documentElement.clientHeight || window.innerHeight;

	const left = Math.max( rect.left, 0 );
	const top = Math.max( rect.top, 0 );
	const right = Math.min( rect.right, vw );
	const bottom = Math.min( rect.bottom, vh );

	const width = Math.max( 0, right - left );
	const height = Math.max( 0, bottom - top );

	const intersectionArea = width * height;
	const viewportArea = vw * vh;

	return viewportArea ? intersectionArea / viewportArea : 0; // 0..1
}
